import React, { useState, FC, useEffect } from "react";
import { Button, Text, Group, TextInput, Loader } from "@mantine/core";
import { editKnowledgeBaseForm, FileType } from "src/interfaces/KnowledgeBase";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import { useManualDocsUpload } from "../../../hooks/useManualKnowledgeBaseDocsUpload";
import { useKnowledgebase } from "src/hooks/useKnowledgebase";
import { ManualUploadKnowledgeBaseEditor } from "./ManualUploadKnowledgeBaseEditor";
import { useQuestionForm } from "src/hooks/useManualQuestionBankDocsUpload";
import { QBQuestionFormEditor } from "./ManualUploadQuestionBankEditor";

type ManualUploadFormProps = {
  onSuccess: (message?: string) => void;
  onError: (error: string) => void;
  editFileData?: editKnowledgeBaseForm;
  setIsEditing: (isEditing: boolean) => void;
};

const ManualUploadEditForm: FC<ManualUploadFormProps> = ({
  onSuccess,
  onError,
  editFileData,
  setIsEditing,
}) => {
  const [editFileDataLocal, _setEditFileDataLocal] = useState<
    editKnowledgeBaseForm | undefined
  >(editFileData || undefined);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formType, _setFormType] = useState<FileType | undefined>(
    editFileData?.type === "Question Bank"
      ? "Question Bank"
      : ("Knowledge" as FileType)
  );

  const {
    getDocs: onRefresh,
    fetchingCombinedSummary,
    getCombinedSummary,
    getUrlsToPostOnAzure,
    uploadFilesToAzure,
    updateManualQuestionBank,
    loading: isKnowledgebaseLoading,
  } = useKnowledgebase(false);

  const {
    themes,
    config,
    addTheme,
    removeTheme,
    updateThemeName,
    addTopic,
    removeTopic,
    updateTopicName,
    addLearningObjective,
    removeLearningObjective,
    updateLearningObjectiveName,
    updateContent,
    validate,
    getFormData,
    prefillForm,
  } = useManualDocsUpload();
  const {
    qbThemes,
    transformedQBData,
    qbConfig,
    addQBTheme,
    removeQBTheme,
    addQBTopic,
    removeQBTopic,
    addQBQuestion,
    removeQBQuestion,
    updateQBQuestion,
    validateQBForm,
    resetQBForm,
    getQBFormData,
    setQBConfigFromStructure,
    createTransformedQB,
    updateSelectedTheme,
    updateSelectedTopic,
    prefillQBForm,
    areThemesEqual,
    getAllNewImagesForEditCase,
    updateThemesWithFileUrls,
    buildStructuredQBThemes,
  } = useQuestionForm();

  useEffect(() => {
    console.log("=== EDIT FORM USEEFFECT ===");
    console.log("editFileData:", editFileData);
    console.log("formType:", formType);

    if (editFileData && editFileData.summary) {
      if (formType === "Knowledge") {
        prefillForm(editFileData.summary);
      } else {
        (async () => {
          console.log("Fetching combined summary for edit...");
          const { data: fetchedSummary, error } = await getCombinedSummary(
            editFileData.level,
            editFileData.subject
          );
          console.log("Combined summary fetched:", { fetchedSummary, error });
          if (fetchedSummary) {
            console.log("Creating transformed QB for edit...");
            const { newQbData } = createTransformedQB(fetchedSummary);
            console.log("Parsed summary:", JSON.parse(editFileData.summary));
            console.log("Calling prefillQBForm for edit with newQbData:", newQbData);
            prefillQBForm(JSON.parse(editFileData.summary), newQbData);
          } else {
            setError(error);
          }
        })();
      }
    }
  }, [editFileData]);

  const renderMainFields = () => (
    <div className="space-yt-2 pb-3">
      <Group position="apart" grow>
        <TextInput
          label="Type"
          size="xs"
          placeholder="Your selected type"
          value={editFileDataLocal?.type}
          disabled
        />
        <TextInput
          label="Subject"
          size="xs"
          placeholder="Your selected subject"
          value={editFileDataLocal?.subject}
          disabled
        />
        <TextInput
          label="Level"
          size="xs"
          placeholder="Your selected level"
          value={editFileDataLocal?.level}
          disabled
        />
      </Group>
      <TextInput
        label="File Name"
        size="xs"
        value={editFileDataLocal?.fileName}
        disabled
      />
    </div>
  );

  const sendQuestionBankPreparedData = async (data: string) => {
    const response = await updateManualQuestionBank(
      true,
      editFileDataLocal?.level!,
      editFileDataLocal?.subject!,
      data,
      editFileDataLocal?.fileName!
    );

    if (response.kind === "ok") {
      // reset form
      onSuccess("Question bank prepared data sent successfully!");
      setIsEditing(false);
    } else {
      setError("Failed to send question bank prepared data");
    }
  };

  const getImageUrls = async () => {
    const fileMap = getAllNewImagesForEditCase();
    const fileUUIDs = Object.keys(fileMap);
    if (!fileUUIDs.length) return;

    const imageUrls = await getUrlsToPostOnAzure(
      editFileDataLocal?.fileName!,
      fileUUIDs
    );
    if (!imageUrls) return;

    const uploadMap = Object.fromEntries(
      fileUUIDs.map((uuid) => [
        uuid,
        {
          uploadUrl: imageUrls[uuid].upload_url,
          file: fileMap[uuid],
        },
      ])
    );
    const readUrls = Object.fromEntries(
      fileUUIDs.map((uuid) => [uuid, imageUrls[uuid].read_url]) // ensure backend provides this
    );
    return { uploadMap, readUrls };
  };

  const uploadImages = async () => {
    const result = await getImageUrls();
    if (!result) return;

    const { uploadMap, readUrls } = result;
    const uploadResult = await uploadFilesToAzure(uploadMap);
    if (uploadResult.kind !== "ok") {
      setError("Image upload failed!");
      return;
    }
    const updatedThemes = updateThemesWithFileUrls(readUrls);
    const buildStructuredQBThemesResult =
      buildStructuredQBThemes(updatedThemes);
    await sendQuestionBankPreparedData(buildStructuredQBThemesResult);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editFileDataLocal) {
      setError("File not found");
      return;
    }

    if (
      (formType === "Knowledge" && !validate()) ||
      (formType === "Question Bank" && !validateQBForm())
    ) {
      setError("Please fill in all required fields");
      return;
    }

    // only for today
    if (formType === "Question Bank") {
      await uploadImages();
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await knowledgeBaseService.modifyFile(
        false,
        editFileDataLocal?.level,
        editFileDataLocal?.subject,
        JSON.stringify(getFormData()),
        editFileDataLocal?.fileName
      );
      onSuccess?.();
      setIsEditing?.(false);

      onRefresh();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const isSomeLoading =
    fetchingCombinedSummary || isLoading || isKnowledgebaseLoading;
  const isFormValid =
    !editFileDataLocal?.subject ||
    !editFileDataLocal?.level ||
    !editFileDataLocal?.type ||
    (formType === "Knowledge" && !validate()) ||
    (formType === "Question Bank" && areThemesEqual()) ||
    !validateQBForm();

  return (
    <form onSubmit={handleSubmit}>
      <div>{renderMainFields()}</div>
      <div className="overflow-y-auto max-h-[calc(100vh-420px)]">
        {isSomeLoading ? (
          <Loader />
        ) : formType === "Knowledge" ? (
          <ManualUploadKnowledgeBaseEditor
            data={{
              themes,
              config,
              addTheme,
              removeTheme,
              updateThemeName,
              addTopic,
              removeTopic,
              updateTopicName,
              addLearningObjective,
              removeLearningObjective,
              updateLearningObjectiveName,
              updateContent,
            }}
          />
        ) : (
          <QBQuestionFormEditor
            data={{
              isEditing: true,
              qbThemes,
              transformedQBData,
              qbConfig,
              addQBTheme,
              removeQBTheme,
              addQBTopic,
              removeQBTopic,
              addQBQuestion,
              removeQBQuestion,
              updateQBQuestion,
              validateQBForm,
              resetQBForm,
              getQBFormData,
              setQBConfigFromStructure,
              createTransformedQB,
              updateSelectedTheme,
              updateSelectedTopic,
            }}
          />
        )}
        {error && (
          <Text color="red" size="sm" mt="sm">
            {error}
          </Text>
        )}
      </div>

      <div>
        <Group position="right" mt="xl">
          {
            <Button
              variant="default"
              onClick={() => setIsEditing?.(false)}
              disabled={isLoading}
            >
              Back
            </Button>
          }
          <Button type="submit" loading={isSomeLoading} disabled={isFormValid}>
            Update
          </Button>
        </Group>
      </div>
    </form>
  );
};

export default ManualUploadEditForm;
